import { DraggableAttributes } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classcat from "classcat";
import React from "react";

import { IconMenu, IconStar } from "@touchtech/web-baselayer-ui";

import css from "./CollectionItem.module.css";
import useManageCollectionItem from "./hooks/useManageCollectionItem";

type Props = {
  id?: string;
  whiteBg?: boolean;
  title: string;
  description?: string;
  onClick: (id: string) => void;
  icon?: JSX.Element;
  showDragHandle?: boolean;
  sortableId?: string;
  /**
   * Marks the collection as managed brand-wide from the admin dashboard. Store staff can open
   * and browse it but cannot change it, so it gets a star badge and no edit affordances.
   */
  isBrandWide?: boolean;
};

const BREAKPOINT_WIDTH = 900;

type SortableProps = {
  setNodeRef: (node: HTMLElement | null) => void;
  style: React.CSSProperties;
  isDragging: boolean;
  listeners: ReturnType<typeof useSortable>["listeners"];
  attributes: DraggableAttributes;
};

type BaseCollectionItemProps = Props & {
  sortableProps?: SortableProps;
};

const BaseCollectionItem = ({
  id = null,
  whiteBg = true,
  title,
  description,
  onClick,
  icon,
  showDragHandle = false,
  isBrandWide = false,
  sortableProps,
}: BaseCollectionItemProps) => {
  const { handleClick, windowWidth, t } = useManageCollectionItem(onClick, id);

  const brandBadgeLabel = t("Managed by brand");

  return (
    <div
      ref={sortableProps?.setNodeRef}
      className={classcat([
        css.container,
        { [css.whiteBg]: whiteBg },
        { [css.dragging]: sortableProps?.isDragging },
      ])}
      style={sortableProps?.style}
      onClick={handleClick}
    >
      <div className={css.titleRow}>
        {isBrandWide && (
          <span
            className={css.brandBadge}
            role="img"
            aria-label={brandBadgeLabel}
            title={brandBadgeLabel}
            data-testid="brand-wide-badge"
          >
            <IconStar />
          </span>
        )}
        <div
          className={classcat([
            css.title,
            {
              "text-headline-4xl": windowWidth > BREAKPOINT_WIDTH,
              "text-headline-3xl": windowWidth <= BREAKPOINT_WIDTH,
            },
          ])}
        >
          {title}
        </div>
        {showDragHandle && sortableProps && (
          <button
            type="button"
            className={css.dragHandle}
            aria-label={t("Drag to reorder")}
            {...sortableProps.listeners}
            {...sortableProps.attributes}
            onClick={(e) => e.stopPropagation()}
          >
            <IconMenu />
          </button>
        )}
      </div>
      {description && (
        <div className={classcat(["text-label-lg", css.description])}>
          {description}
        </div>
      )}
      <div className={classcat([css.iconContainer])}>{icon}</div>
    </div>
  );
};

const SortableCollectionItem = (props: Props) => {
  const { sortableId, id, showDragHandle } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId || id || "unsortable",
    disabled: !showDragHandle,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <BaseCollectionItem
      {...props}
      sortableProps={{
        setNodeRef,
        style,
        isDragging,
        listeners,
        attributes,
      }}
    />
  );
};

const CollectionItem = (props: Props) => {
  if (props.sortableId) {
    return <SortableCollectionItem {...props} />;
  }

  return <BaseCollectionItem {...props} />;
};

export default CollectionItem;
